/* Pure cart helpers for the customer shop.
 *
 * Plan §"Variants": cart line identity is `(productId, variantId | null)`.
 * Functions are pure and immutable so they're trivially testable and safe to
 * use inside `useReducer` / `setState` callbacks.
 *
 * Why local (not shared with kiosk)?
 *  - `pi-kiosk-shared` `CartItem` does not carry `variantId` and the kiosk
 *    flow ignores variants in checkout today (see plan: kiosk-parity-followup).
 *  - The customer shop ships variant-aware lines without changing the kiosk
 *    contract; consolidation into `pi-kiosk-shared` is a follow-up.
 */

import type {
  ShopCart,
  ShopCartLine,
  ShopCartLineProduct,
  ShopCartLineVariant,
} from '../types.js';
import { toMinorUnits } from './money.js';

export function buildLineKey(productId: number, variantId: number | null): string {
  return variantId !== null ? `${productId}:${variantId}` : `${productId}:base`;
}

function recomputeTotals(lines: ShopCartLine[]): ShopCart {
  let totalAmountMajor = 0;
  let totalQuantity = 0;
  for (const line of lines) {
    totalAmountMajor += line.unitPrice * line.quantity;
    totalQuantity += line.quantity;
  }
  // Round to 2dp consistently with the backend Money value object so preview
  // totals never disagree with what the server will recompute in Phase B.
  const totalAmountMinor = toMinorUnits(totalAmountMajor);
  return {
    lines,
    totalAmountMajor: totalAmountMinor / 100,
    totalAmountMinor,
    totalQuantity,
  };
}

export function createEmptyCart(): ShopCart {
  return recomputeTotals([]);
}

export interface AddLineInput {
  product: ShopCartLineProduct;
  variant?: ShopCartLineVariant | null;
  unitPrice: number;
  quantity?: number;
}

/** Stack lines by (productId, variantId|null); else append. */
export function addLine(cart: ShopCart, input: AddLineInput): ShopCart {
  const variant = input.variant ?? null;
  const quantity = input.quantity ?? 1;
  if (quantity <= 0) {
    return cart;
  }
  const key = buildLineKey(input.product.id, variant ? variant.id : null);
  const existingIdx = cart.lines.findIndex((line) => line.key === key);
  if (existingIdx === -1) {
    const newLine: ShopCartLine = {
      key,
      product: input.product,
      variant,
      quantity,
      unitPrice: input.unitPrice,
    };
    return recomputeTotals([...cart.lines, newLine]);
  }
  const existing = cart.lines[existingIdx];
  if (existing === undefined) {
    return cart;
  }
  const updated: ShopCartLine = {
    ...existing,
    quantity: existing.quantity + quantity,
  };
  const nextLines = [...cart.lines];
  nextLines[existingIdx] = updated;
  return recomputeTotals(nextLines);
}

export function removeLine(cart: ShopCart, key: string): ShopCart {
  const next = cart.lines.filter((line) => line.key !== key);
  if (next.length === cart.lines.length) {
    return cart;
  }
  return recomputeTotals(next);
}

export function setLineQuantity(
  cart: ShopCart,
  key: string,
  quantity: number
): ShopCart {
  if (quantity <= 0) {
    return removeLine(cart, key);
  }
  const idx = cart.lines.findIndex((line) => line.key === key);
  if (idx === -1) {
    return cart;
  }
  const existing = cart.lines[idx];
  if (existing === undefined || existing.quantity === quantity) {
    return cart;
  }
  const next = [...cart.lines];
  next[idx] = { ...existing, quantity };
  return recomputeTotals(next);
}

/** Update stored unit price for a line (Stage 7 price revalidation sync). */
export function setLineUnitPrice(cart: ShopCart, key: string, unitPrice: number): ShopCart {
  const idx = cart.lines.findIndex((line) => line.key === key);
  if (idx === -1) {
    return cart;
  }
  const existing = cart.lines[idx];
  if (existing === undefined || existing.unitPrice === unitPrice) {
    return cart;
  }
  const next = [...cart.lines];
  next[idx] = { ...existing, unitPrice };
  return recomputeTotals(next);
}

export function getLineQuantity(
  cart: ShopCart,
  productId: number,
  variantId: number | null
): number {
  const key = buildLineKey(productId, variantId);
  const line = cart.lines.find((l) => l.key === key);
  return line ? line.quantity : 0;
}
